import React from 'react';
import { Box, Typography } from '@mui/material';
import { spacing, typography, iconSizes, semanticColors } from 'design-system';

/**
 * IconLabelCard Props
 */
export interface IconLabelCardProps {
  /** Icon element */
  icon: React.ReactNode;
  
  /** Label text (e.g., "3 Beds") */
  label: string;
  
  /** Whether to blur the label (icon remains visible) */
  blur?: boolean;
  
  /** Border radius (default: '16px') */
  borderRadius?: string;
  
  /** Click handler (optional) */
  onClick?: () => void;
  
  /** Additional CSS class */
  className?: string;
}

/**
 * IconLabelCard Component
 * 
 * Displays an icon with label in a rectangular card (desktop variant).
 * 
 * Design Specs (per Figma node 1080-23982):
 * - Rectangular card with 16px border radius
 * - Grey background (semanticColors.cardBg)
 * - Icon on top, label below
 * - 6px gap between icon and label
 * - 18px vertical, 20px horizontal padding
 * - Centered content
 * 
 * @example
 * ```tsx
 * <IconLabelCard icon={<BedIcon />} label="3 Beds" />
 * ```
 */
export default function IconLabelCard({
  icon,
  label,
  blur,
  borderRadius = '16px',
  onClick,
  className,
}: IconLabelCardProps) {
  return (
    <Box
      className={className}
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.lg, // 12px gap between icon and label
        padding: '18px 20px', // Per Figma: py-18px, px-20px
        borderRadius,
        backgroundColor: semanticColors.cardBg, // Grey background per Figma
        minWidth: '100px',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          opacity: 0.9,
        } : {},
        '& svg': {
          width: iconSizes.mdLg,
          height: iconSizes.mdLg,
          display: 'block',
        },
      }}
    >
      {icon}
      <Typography
        variant="body2"
        sx={{
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.medium,
          lineHeight: typography.lineHeight.base,
          textAlign: 'center',
          ...(blur && {
            filter: 'blur(6px)',
            userSelect: 'none',
          }),
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
